import { Injectable, inject, signal } from '@angular/core';
import { supabase } from '../supabase';
import { Operator, Municipality, UserRole } from '../models';

@Injectable({
    providedIn: 'root'
})
export class OperatorService {
    public operatorsTable = signal<Operator[]>([]);

    async refreshOperators(municipalityId?: string) {
        let query = supabase.from('operators').select('id, name, login, municipality_id, role, location_id, is_active, auth_user_id, has_seen_tutorial, created_at');
        if (municipalityId) {
            query = query.eq('municipality_id', municipalityId);
        }
        const { data, error } = await query.order('name');
        if (error) throw error;

        const mapped: Operator[] = (data || []).map(o => this.mapDbToOperator(o));
        this.operatorsTable.set(mapped);
        return mapped;
    }

    async loadOperators() {
        return this.refreshOperators();
    }

    async createOperator(
        name: string,
        municipalityId: string,
        login: string,
        password: string,
        role: string = 'OPERADOR_MUNICIPAL',
        locationId: number | null = null,
        authUserId?: string // kept for API compatibility but handled by RPC
    ) {
        const cleanLogin = login.trim();

        // Uses secure_upsert_operator RPC which writes directly to auth.users
        // with email_confirmed_at = NOW(), bypassing SMTP entirely.
        // Works on self-hosted Supabase without email confirmation configured.
        const { data: rpcData, error: rpcError } = await supabase.rpc('secure_upsert_operator', {
            p_name: name,
            p_login: cleanLogin,
            p_password: password,
            p_municipality_id: municipalityId,
            p_role: role,
            p_location_id: locationId?.toString() ?? null
        });

        if (rpcError) {
            console.error('[OperatorService] secure_upsert_operator error:', rpcError);
            if (rpcError.code === '23505') throw new Error(`Conflito: O login "${cleanLogin}" já está em uso.`);
            throw rpcError;
        }
        if (!rpcData?.success) throw new Error('Erro ao criar operador no sistema de autenticação.');

        // Fetch the full operator record created/updated by the RPC
        const { data, error } = await supabase
            .from('operators')
            .select('id, name, login, municipality_id, role, location_id, is_active, auth_user_id, has_seen_tutorial, created_at')
            .eq('id', rpcData.operator_id)
            .single();

        if (error) throw error;
        const newOp = this.mapDbToOperator(data);
        this.operatorsTable.update(ops => [...ops, newOp]);
        return newOp;
    }

    async updateOperatorStatus(id: number, isActive: boolean) {
        const { error } = await supabase
            .from('operators')
            .update({ is_active: isActive })
            .eq('id', id);

        if (error) throw error;
        this.operatorsTable.update(ops => ops.map(o => o.id === id ? { ...o, isActive } : o));
    }

    async deleteOperator(id: number) {
        // 1. Handle dependent ACS record first to avoid Foreign Key violations
        const { error: acsError } = await supabase
            .from('acs')
            .delete()
            .eq('operator_id', id);

        if (acsError) {
            console.warn('[OperatorService] Failed to delete linked ACS record:', acsError);
        }

        // 2. Try to delete from Supabase Auth - may fail if operator has no auth_user_id (409 Conflict)
        const { error } = await supabase.rpc('delete_operator_auth_user', {
            p_operator_id: id
        });

        // 409 Conflict = no auth_user_id linked or already removed, proceed with DB-only delete
        if (error && error.code !== '409' && !error.message?.includes('Conflict') && !error.message?.includes('not found')) {
            throw error;
        }

        // 3. Always delete from operators table
        const { error: dbError } = await supabase.from('operators').delete().eq('id', id);
        if (dbError) throw dbError;

        this.operatorsTable.update(ops => ops.filter(o => o.id !== id));
    }

    async updateOperator(id: number, updates: Partial<Operator> & { password?: string, location_id?: number }) {
        const dbUpdates: any = {
            name: updates.name,
            login: updates.login,
            municipality_id: updates.municipalityId,
        };

        if (updates.location_id !== undefined) {
            dbUpdates.location_id = updates.location_id;
        }

        if (updates.role) {
            dbUpdates.role = updates.role;
        }

        if (updates.password) {
            dbUpdates.password = updates.password;
        }

        const { data, error } = await supabase
            .from('operators')
            .update(dbUpdates)
            .eq('id', id)
            .select()
            .maybeSingle();

        if (error) throw error;

        const updatedOp = this.mapDbToOperator(data);
        this.operatorsTable.update(ops => ops.map(o => o.id === id ? updatedOp : o));
        return updatedOp;
    }

    async updatePassword(currentPass: string, newPass: string, operatorId: number) {
        // 1. Verify current password first
        const { data: op, error: fetchError } = await supabase
            .from('operators')
            .select('password')
            .eq('id', operatorId)
            .single();

        if (fetchError) throw new Error('Erro ao verificar senha atual.');
        if (op && op.password !== currentPass) throw new Error('Senha atual incorreta.');

        // 2. Try Supabase Update User (if authenticated)
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user) {
            await supabase.auth.updateUser({ password: newPass });
        }

        // 3. Update the legacy password field in public.operators
        const { error: updateError } = await supabase
            .from('operators')
            .update({ password: newPass })
            .eq('id', operatorId);

        if (updateError) throw new Error('Erro ao atualizar senha: ' + updateError.message);

        return { success: true };
    }

    async authenticateOperator(login: string, pass: string) {
        const email = `${login.trim()}@filasaude.com`;

        // 1. Try Direct Auth Session first (Modern way)
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: email,
            password: pass
        });

        if (!authError && authData.user) {
            const { data: opData, error: opError } = await supabase
                .from('operators')
                .select('id, name, login, municipality_id, role, location_id, is_active, auth_user_id, has_seen_tutorial, created_at')
                .eq('auth_user_id', authData.user.id)
                .maybeSingle();

            if (!opError && opData) {
                const operator = this.mapDbToOperator(opData);
                const { data: mun } = await supabase.from('municipalities').select('*').eq('id', operator.municipalityId).single();
                return {
                    operator,
                    municipality: { id: mun.id, name: mun.name, isActive: mun.is_active, logo_url: mun.logo_url }
                };
            }
        }

        // 2. Fallback: Legacy Authentication (Checking credentials via Security Definer RPC)
        // Since RLS is now enabled on 'operators' table, anonymous direct selects are blocked.
        console.log('[OperatorService] Fallback RPC call:', { login, p_login: login, p_password_length: pass.length });
        const { data: legacyResult, error: rpcError } = await supabase.rpc('authenticate_operator', {
            p_login: login,
            p_password: pass
        });

        if (rpcError) {
            console.error('[OperatorService] RPC Error:', rpcError);
            throw new Error('Login ou senha incorreto.');
        }

        const legacyData = legacyResult.operator;
        const legacyMun = legacyResult.municipality;

        // 3. Auto-Migration: create Supabase Auth account using secure RPC (no SMTP required)
        if (!legacyData.auth_user_id) {
            console.log('[OperatorService] Auto-migração via RPC segura para:', email);
            const { data: rpcData, error: rpcError } = await supabase.rpc('secure_upsert_operator', {
                p_name: legacyData.name,
                p_login: login.trim(),
                p_password: pass,
                p_municipality_id: legacyData.municipalityId,
                p_role: legacyData.role,
                p_location_id: (legacyData.location_id || legacyData.locationId)?.toString() ?? null
            });

            if (!rpcError && rpcData?.success) {
                await supabase.from('operators').update({ auth_user_id: rpcData.auth_user_id }).eq('id', legacyData.id);
                legacyData.auth_user_id = rpcData.auth_user_id;
            } else {
                console.warn('[OperatorService] Auto-migração incompleta:', rpcError?.message);
            }
        } else if (authError) {
            // Has auth_user_id but signInWithPassword failed - passwords out of sync
            throw new Error('Suas credenciais de segurança estão desatualizadas. Por favor, contate o administrador.');
        }

        // 4. Always try to establish a Supabase session via signInWithPassword
        const { data: finalAuth } = await supabase.auth.signInWithPassword({ email, password: pass });
        if (!finalAuth?.session) {
            console.warn('[OperatorService] Sessão Supabase não disponível após auto-migração. Funcionalidade pode ser limitada.');
        }

        const { data: mun } = await supabase.from('municipalities').select('*').eq('id', legacyData.municipalityId).maybeSingle();
        if (!mun) throw new Error('Erro ao identificar município.');

        return {
            operator: this.mapDbToOperator({
                ...legacyData,
                municipality_id: legacyData.municipalityId,
                is_active: legacyData.isActive,
                auth_user_id: legacyData.auth_user_id || legacyData.authUserId
            }),
            municipality: { id: mun.id, name: mun.name, isActive: mun.is_active, logo_url: mun.logo_url }
        };
    }

    async updateTutorialStatus(operatorId: number, status: boolean): Promise<void> {
        const { error } = await supabase
            .from('operators')
            .update({ has_seen_tutorial: status })
            .eq('id', operatorId);

        if (error) {
            console.error('Error updating tutorial status:', error);
            throw error;
        }
    }

    public mapDbToOperator(o: any): Operator {
        return {
            id: o.id,
            name: o.name,
            login: o.login,
            isActive: o.is_active,
            municipalityId: o.municipality_id,
            role: o.role as UserRole,
            location_id: o.location_id,
            createdAt: new Date(o.created_at),
            authUserId: o.auth_user_id,
            hasSeenTutorial: o.has_seen_tutorial || false
        };
    }
}

